import type { ScopedBinding } from "@/app/types";
import { BFF_URL } from "./const";
import { type ApiClient, createApiClient } from "./http-client";
import type { OrgProjectScope } from "./resource-context";

export type { ApiClient } from "./http-client";

type OrganizationScope = Pick<OrgProjectScope, "organizationId">;

function hasProjectScope(
  scope: OrganizationScope | OrgProjectScope
): scope is OrgProjectScope {
  return "projectId" in scope && typeof scope.projectId === "string";
}

const ENGINE_BASE_URL = `${BFF_URL}/api/engine`;
const COORDINATOR_BASE_URL = `${BFF_URL}/api/coordinator`;

// Unscoped experience API routes such as `/api/config/*`.
export const experienceApi = createApiClient({ baseURL: BFF_URL });
export const coordinatorRootApi = createApiClient({
  baseURL: COORDINATOR_BASE_URL,
});

const createProjectEngineApi = (scope: OrgProjectScope): ApiClient => {
  const { organizationId, projectId } = scope;
  return createApiClient({
    baseURL: `${ENGINE_BASE_URL}/orgs/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}`,
  });
};

const createProjectCoordinatorApi = (scope: OrgProjectScope): ApiClient => {
  const { organizationId, projectId } = scope;
  return createApiClient({
    baseURL: `${COORDINATOR_BASE_URL}/orgs/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}`,
  });
};

const createOrganizationEngineApi = (scope: OrgProjectScope): ApiClient => {
  // Get both org and project context - we need project_id for permission checks
  // even though we're using tenant-level bindings for the data
  const { organizationId, projectId } = scope;

  return createApiClient({
    baseURL: `${ENGINE_BASE_URL}/orgs/${encodeURIComponent(organizationId)}`,
    defaultParams: { project_id: projectId },
  });
};

const createOrganizationCoordinatorApi = (
  scope: OrganizationScope
): ApiClient => {
  const { organizationId } = scope;
  return createApiClient({
    baseURL: `${COORDINATOR_BASE_URL}/orgs/${encodeURIComponent(organizationId)}`,
  });
};

export function getEngineApi(
  scope: OrgProjectScope,
  binding: ScopedBinding = "project"
): ApiClient {
  return binding === "tenant"
    ? createOrganizationEngineApi(scope)
    : createProjectEngineApi(scope);
}

export function getCoordinatorApi(
  scope: OrganizationScope | OrgProjectScope
): ApiClient {
  return hasProjectScope(scope)
    ? createProjectCoordinatorApi(scope)
    : createOrganizationCoordinatorApi(scope);
}
